/**
 * CreatePollModal - Create a new poll for a trip.
 * Design: tripways.pen node sZHbw (desktop) / Z32Zxu (mobile) — reuses Modal's
 * dialog(desktop)/bottom-sheet(mobile) auto-adapting variant.
 */

import { useEffect, useState } from 'react';
import { GripVertical, MapPin, Plus, X } from 'lucide-react';
import Modal from '../common/Modal';
import { apiGet } from '../../services/apiClient';
import type { TripDetailResponse, ItineraryDTO } from '../../types/trip';
import type { CreatePollOptionInput, VoteType } from '../../types/poll';

interface OptionDraft {
  key: string;
  label: string;
  itineraryId?: string;
}

interface CreatePollModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: string;
  onSubmit: (input: {
    subject: string;
    voteType: VoteType;
    expiresAt?: string | null;
    options: CreatePollOptionInput[];
  }) => Promise<void>;
}

let keyCounter = 0;
const nextKey = () => `opt-${++keyCounter}`;

export default function CreatePollModal({ isOpen, onClose, tripId, onSubmit }: CreatePollModalProps) {
  const [subject, setSubject] = useState('');
  const [voteType, setVoteType] = useState<VoteType>('SINGLE');
  const [options, setOptions] = useState<OptionDraft[]>([{ key: nextKey(), label: '' }, { key: nextKey(), label: '' }]);
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [unplanned, setUnplanned] = useState<ItineraryDTO[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setSubject('');
    setVoteType('SINGLE');
    setOptions([{ key: nextKey(), label: '' }, { key: nextKey(), label: '' }]);
    setExpiresAt('');
    setShowPicker(false);

    apiGet<TripDetailResponse>(`/trip/${tripId}`).then((response) => {
      if (response.exitCode === 1 && response.data) {
        setUnplanned(response.data.unplannedItineraries);
      }
    });
  }, [isOpen, tripId]);

  if (!isOpen) return null;

  const updateLabel = (key: string, label: string) => {
    setOptions((prev) => prev.map((o) => (o.key === key ? { ...o, label } : o)));
  };

  const removeOption = (key: string) => {
    setOptions((prev) => prev.filter((o) => o.key !== key));
  };

  const addOption = () => {
    setOptions((prev) => [...prev, { key: nextKey(), label: '' }]);
  };

  const pickFromItinerary = (itinerary: ItineraryDTO) => {
    if (options.some((o) => o.itineraryId === itinerary.itineraryId)) return;
    setOptions((prev) => {
      const emptyIndex = prev.findIndex((o) => o.label.trim() === '' && !o.itineraryId);
      if (emptyIndex !== -1) {
        const next = [...prev];
        next[emptyIndex] = { ...next[emptyIndex], label: itinerary.title, itineraryId: itinerary.itineraryId };
        return next;
      }
      return [...prev, { key: nextKey(), label: itinerary.title, itineraryId: itinerary.itineraryId }];
    });
    setShowPicker(false);
  };

  const validOptions = options.filter((o) => o.label.trim().length > 0);
  const canSubmit = subject.trim().length > 0 && validOptions.length >= 2 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit({
        subject: subject.trim(),
        voteType,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        options: validOptions.map((o) => ({ label: o.label.trim(), itineraryId: o.itineraryId })),
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="發起投票" variant="bottom-sheet">
      <div className="flex flex-col max-h-[75vh] md:max-h-[80vh]">
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-6">
          {/* Subject */}
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-semibold text-slate-900">投票主題</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="例如：晚餐要吃什麼？"
              className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] focus:outline-none focus:border-primary"
            />
          </div>

          {/* Type toggle */}
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-semibold text-slate-900">投票類型</label>
            <div className="flex rounded-[10px] bg-slate-100 p-[3px] h-10">
              {(['SINGLE', 'MULTI'] as VoteType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => setVoteType(type)}
                  className={`flex-1 rounded-lg text-sm font-medium transition-colors ${
                    voteType === type ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  {type === 'SINGLE' ? '單選' : '多選'}
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center justify-between">
              <label className="text-[13px] font-semibold text-slate-900">選項</label>
              <span className="text-xs text-slate-400">至少 2 個選項</span>
            </div>

            {options.map((option) => (
              <div key={option.key} className="flex items-center gap-2.5">
                <GripVertical size={16} className="text-slate-300 shrink-0" />
                <input
                  value={option.label}
                  onChange={(e) => updateLabel(option.key, e.target.value)}
                  placeholder="輸入選項"
                  className="flex-1 h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] focus:outline-none focus:border-primary"
                />
                {options.length > 2 && (
                  <button
                    onClick={() => removeOption(option.key)}
                    className="flex items-center justify-center w-11 h-11 -mr-2.5 text-slate-300 hover:text-slate-500 shrink-0"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={addOption}
              className="flex items-center justify-center gap-1.5 h-11 rounded-[10px] border border-dashed border-slate-200 text-primary text-sm"
            >
              <Plus size={16} />
              新增選項
            </button>

            <button onClick={() => setShowPicker((v) => !v)} className="text-[13px] text-primary text-center">
              從未排期行程中選擇
            </button>

            {showPicker && (
              <div className="rounded-[10px] border border-slate-200 max-h-40 overflow-y-auto">
                {unplanned.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">尚無未排期行程</p>
                ) : (
                  unplanned.map((itinerary) => (
                    <button
                      key={itinerary.itineraryId}
                      onClick={() => pickFromItinerary(itinerary)}
                      className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <MapPin size={14} className="text-slate-400 shrink-0" />
                      {itinerary.title}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Expire */}
          <div className="flex flex-col gap-2">
            <label className="text-[13px] font-semibold text-slate-900">截止時間（選填）</label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full h-11 rounded-[10px] border border-slate-200 px-3.5 text-[15px] focus:outline-none focus:border-primary"
            />
            <p className="text-xs text-slate-400">留空代表不自動截止，需手動關閉投票</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="rounded-[10px] border border-slate-200 px-5 py-2.5 text-sm text-slate-600">
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-[10px] bg-primary px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
          >
            建立投票
          </button>
        </div>
      </div>
    </Modal>
  );
}
